import {
  ActionList,
  ActionListItem,
  Box,
  Button,
  Icon,
  IconButton,
} from "@prismicio/editor-ui"
import { useState } from "react"

import { useAPIExplorer } from "../../../APIExplorerContext"
import { RequestFormInputBase } from "../RequestFormInputBase"
import { OrderingConfigDialog } from "./OrderingConfigDialog"

/**
 * Display a fieldset managing a request orderings setting.
 */
export function RequestOrderingsFieldset() {
  const { request } = useAPIExplorer()

  return request.advanced ? <RequestOrderingsFieldsetBase /> : null
}

function RequestOrderingsFieldsetBase() {
  const { request } = useAPIExplorer()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editIndex, setEditIndex] = useState<number | undefined>()

  function addOrdering() {
    setEditIndex(undefined)
    setDialogOpen(true)
  }

  function editOrdering(index: number) {
    const maybeOrdering = request.orderings[index]
    if (!maybeOrdering) return

    setEditIndex(index)
    setDialogOpen(true)
  }

  function removeOrdering(index: number) {
    request.setOrderings([
      ...request.orderings.slice(0, index),
      ...request.orderings.slice(index + 1),
    ])
  }

  return (
    <RequestFormInputBase label="ORDERINGS">
      <Box display="grid" gap={4}>
        <ActiveOrderings
          editOrdering={editOrdering}
          removeOrdering={removeOrdering}
        />
        <Button startIcon="add" color="grey" onClick={addOrdering}>
          Add
        </Button>
        <OrderingConfigDialog
          key={editIndex ?? "add"}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          editIndex={editIndex}
        />
      </Box>
    </RequestFormInputBase>
  )
}

type ActiveOrderingsProps = {
  editOrdering: (index: number) => void
  removeOrdering: (index: number) => void
}

function ActiveOrderings(props: ActiveOrderingsProps) {
  const { editOrdering, removeOrdering } = props
  const { request } = useAPIExplorer()

  if (!request.orderings.length) return null

  return (
    <ActionList variant="compact-bordered" sx={{ overflowY: "hidden" }}>
      {request.orderings.map((ordering, index) => (
        <ActionListItem
          key={`${index}-${JSON.stringify(ordering)}`}
          onClick={() => editOrdering(index)}
          renderStartIcon={() => (
            <Icon
              name={
                ordering.direction === "desc" ? "arrowDownward" : "arrowUpward"
              }
              color="grey11"
              size="small"
            />
          )}
          endOverlay={
            <IconButton
              icon="delete"
              variant="ghost"
              size="small"
              onClick={() => removeOrdering(index)}
            />
          }
          oneLine
          noWrapTitle
        >
          {ordering.field}
        </ActionListItem>
      ))}
    </ActionList>
  )
}
